import React from 'react';
import { Dialog, DialogContent, Typography, Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Fade for the full-screen overlay
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const floatCycle = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) translate(var(--x0), var(--y0)) scale(0.96);
    filter: blur(8px);
  }
  4% {
    opacity: 0.98;
    transform: translate(-50%, -50%) translate(var(--x1), var(--y1)) scale(1);
    filter: blur(0);
  }
  26% {
    opacity: 1;
    transform: translate(-50%, -50%) translate(var(--x1), var(--y1)) scale(1);
  }
  30% {
    opacity: 0;
    transform: translate(-50%, -50%) translate(var(--x0), var(--y0)) scale(0.98);
    filter: blur(6px);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) translate(var(--x0), var(--y0)) scale(0.96);
    filter: blur(8px);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const spinReverse = keyframes`
  to { transform: translate(-50%, -50%) rotate(-360deg); }
`;

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    background: 'rgba(0, 0, 0, 0)',
    boxShadow: 'none',
    maxWidth: 'none',
    maxHeight: 'none',
    margin: 0,
    width: '100%',
    height: '100%',
    borderRadius: 0,
    animation: `${fadeIn} 0.25s ease-out`,
  },
}));

const FullscreenStage = styled(DialogContent)(({ theme }) => ({
  position: 'relative',
  padding: 0,
  height: '100vh',
  overflow: 'hidden',
  display: 'block',
  background:
    'radial-gradient(1100px 520px at 72% -10%, rgba(11, 145, 214, 0.14), transparent 65%),\
radial-gradient(760px 460px at -12% 82%, rgba(52, 168, 83, 0.16), transparent 60%),\
linear-gradient(180deg, #f8fbff 0%, #e5f1ff 100%)',
  fontFamily: 'Inter, sans-serif',
  color: '#0f172a',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -2,
    background:
      'radial-gradient(520px 520px at 50% 50%, rgba(15, 23, 42, 0.06), transparent 70%),\
repeating-linear-gradient(0deg, transparent 0, transparent 28px, rgba(15, 23, 42, 0.045) 28px, rgba(15, 23, 42, 0.045) 29px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
}));

const CenterWrapper = styled(Box)(() => ({
  position: 'relative',
  zIndex: 3,
  width: '100%',
  height: '100%',
  display: 'grid',
  placeItems: 'center',
}));

const Card = styled(Box)(() => ({
  background: 'rgba(255, 255, 255, 0.95)',
  padding: '56px 64px',
  borderRadius: 18,
  boxShadow: '0 16px 44px rgba(15, 23, 42, 0.18)',
  textAlign: 'center',
  maxWidth: 420,
  width: 'calc(100vw - 64px)',
}));

const Loader = styled(Box)(() => ({
  position: 'relative',
  width: 120,
  height: 120,
  margin: 'auto',
}));

const OuterRing = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  background:
    'conic-gradient(from 12deg, #0B86CE 0 90deg, transparent 90deg 160deg, #0B86CE 160deg 320deg, transparent 320deg 360deg)',
  WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 8px), #000 calc(100% - 8px))',
  mask: 'radial-gradient(farthest-side, transparent calc(100% - 8px), #000 calc(100% - 8px))',
  animation: `${spin} 1.2s linear infinite`,
}));

const InnerRing = styled(Box)(() => ({
  position: 'absolute',
  width: 74,
  height: 74,
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  background:
    'conic-gradient(from -42deg, #34A853 0 120deg, transparent 120deg 210deg, #34A853 210deg 350deg, transparent 350deg 360deg)',
  WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 6px))',
  mask: 'radial-gradient(farthest-side, transparent calc(100% - 6px), #000 calc(100% - 6px))',
  animation: `${spinReverse} 1.2s linear infinite`,
}));

const Caption = styled(Typography)(() => ({
  marginTop: 28,
  fontSize: 20,
  letterSpacing: 0.2,
  color: '#0f172a',
  fontWeight: 600,
}));

const SubCaption = styled(Typography)(() => ({
  marginTop: 12,
  fontSize: 14,
  color: '#4a6078',
}));

const FloatingHeadline = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  padding: '14px 18px',
  borderRadius: 14,
  background: 'rgba(255, 255, 255, 0.72)',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  backdropFilter: 'blur(14px) saturate(130%)',
  color: '#0f172a',
  width: 'clamp(240px, 42vw, 520px)',
  boxShadow: '0 12px 32px rgba(15, 23, 42, 0.14)',
  fontSize: 'clamp(14px, 1.9vw, 18px)',
  lineHeight: 1.32,
  opacity: 0,
  willChange: 'transform, opacity, filter',
  animation: `${floatCycle} 80s linear infinite`,
  animationDelay: 'var(--delay, 0s)',
  zIndex: 2,
  pointerEvents: 'none',
  '& strong': {
    color: theme.palette.primary.main,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: '50%',
    top: '50%',
    background:
      'radial-gradient(circle at 35% 35%, #fff, #fff 30%, transparent 40%),\
radial-gradient(circle at 70% 70%, var(--dot-color, #0B91D6), transparent 48%)',
    boxShadow: '0 0 20px var(--glow-color, rgba(11, 145, 214, 0.45))',
    transform: 'translateY(-50%)',
  },
  '&.left': {
    '--x0': '-62vw',
    '--y0': 'var(--lane)',
    '--x1': '-24rem',
    '--y1': 'var(--lane)',
    '--dot-color': '#0B91D6',
    '--glow-color': 'rgba(11, 145, 214, 0.55)',
    borderLeft: '3px solid #0B91D6',
    paddingLeft: 24,
    '&::before': {
      left: 12,
    },
  },
  '&.right': {
    '--x0': '62vw',
    '--y0': 'var(--lane)',
    '--x1': '24rem',
    '--y1': 'var(--lane)',
    '--dot-color': '#34A853',
    '--glow-color': 'rgba(52, 168, 83, 0.45)',
    borderRight: '3px solid #34A853',
    paddingRight: 24,
    textAlign: 'right',
    '&::before': {
      right: 12,
      left: 'unset',
    },
  },
  '&.top': {
    '--x0': 'var(--lane)',
    '--y0': '-62vh',
    '--x1': 'var(--lane)',
    '--y1': '-22rem',
    '--dot-color': '#f59e0b',
    '--glow-color': 'rgba(245, 158, 11, 0.45)',
    borderTop: '3px solid #f59e0b',
    paddingTop: 22,
    '&::before': {
      top: 12,
      left: '50%',
      transform: 'translate(-50%, 0)',
    },
  },
  '&.bottom': {
    '--x0': 'var(--lane)',
    '--y0': '62vh',
    '--x1': 'var(--lane)',
    '--y1': '22rem',
    '--dot-color': '#6366f1',
    '--glow-color': 'rgba(99, 102, 241, 0.45)',
    borderBottom: '3px solid #6366f1',
    paddingBottom: 22,
    '&::before': {
      bottom: 12,
      left: '50%',
      top: 'unset',
      transform: 'translate(-50%, 0)',
    },
  },
  [theme.breakpoints.down('md')]: {
    width: 'min(90vw, 420px)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 14,
    '&.left': {
      '--x1': '-16rem',
    },
    '&.right': {
      '--x1': '16rem',
    },
    '&.top': {
      '--y1': '-16rem',
    },
    '&.bottom': {
      '--y1': '16rem',
    },
  },
}));

const HEADLINES = [
  {
    id: 'left-1',
    orientation: 'left',
    lane: '-14rem',
    delay: '0s',
    content: (
      <>
        <strong>Precision sync.</strong> We are fetching live Google Business Profile insights for your selected account.
      </>
    ),
  },
  {
    id: 'right-1',
    orientation: 'right',
    lane: '-12rem',
    delay: '10s',
    content: (
      <>
        Profiles with complete information drive <strong>2x more discovery impressions</strong>. We make sure nothing is missing.
      </>
    ),
  },
  {
    id: 'top-1',
    orientation: 'top',
    lane: '-16rem',
    delay: '20s',
    content: (
      <>
        <strong>Keyword coverage</strong> is benchmarked against your top three focus terms to highlight the biggest gaps.
      </>
    ),
  },
  {
    id: 'bottom-1',
    orientation: 'bottom',
    lane: '14rem',
    delay: '30s',
    content: (
      <>
        Businesses that update photos monthly see <strong>35%</strong> higher engagement - we check how you compare.
      </>
    ),
  },
  {
    id: 'left-2',
    orientation: 'left',
    lane: '2rem',
    delay: '40s',
    content: (
      <>
        <strong>Review signals</strong> and response speed contribute up to 30% of your profile strength - analysis in progress.
      </>
    ),
  },
  {
    id: 'right-2',
    orientation: 'right',
    lane: '8rem',
    delay: '50s',
    content: (
      <>
        Competitive density in your service area is mapped to show where you can outrank nearby businesses.
      </>
    ),
  },
  {
    id: 'top-2',
    orientation: 'top',
    lane: '0rem',
    delay: '60s',
    content: (
      <>
        We blend <strong>Google signals</strong> with Visibeen benchmarks so your action plan is ready the moment loading ends.
      </>
    ),
  },
  {
    id: 'bottom-2',
    orientation: 'bottom',
    lane: '-8rem',
    delay: '70s',
    content: (
      <>
        Only <strong>18%</strong> of local competitors optimise their listings monthly - this audit keeps you ahead.
      </>
    ),
  },
];

const AnalysisLoadingPopup = ({ open }) => {
  return (
    <StyledDialog open={open} disableEscapeKeyDown>
      <FullscreenStage>
        <CenterWrapper>
          <Card>
            <Loader>
              <OuterRing />
              <InnerRing />
            </Loader>
            <Caption>Analyzing Your Profile Strength...</Caption>
            <SubCaption>Hang tight while we compile fresh insights from Google and Visibeen benchmarks.</SubCaption>
          </Card>
        </CenterWrapper>
        {HEADLINES.map((headline) => (
          <FloatingHeadline
            key={headline.id}
            className={headline.orientation}
            style={{ '--delay': headline.delay, '--lane': headline.lane }}
          >
            {headline.content}
          </FloatingHeadline>
        ))}
      </FullscreenStage>
    </StyledDialog>
  );
};

export default AnalysisLoadingPopup;














